import React, { useEffect, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";

import { useAppContext } from "@/App";
import { Awesome, ParseFormErrors } from "@components/Helpers";
import { ModalDelete, ModalFilter } from "@components/Modals";
import DjangoPaginator from "@components/Paginator";
import { ScrollToElement, is_dict, is_str, parseAndLinkify } from "@components/Utils";

export function ListingCard({ children, header, headerTools, filterComponent }) {
    const { apiData, setApiData } = useAppContext();
    const [showFilter, setShowFilter] = useState(false);
    const prefix = "filter";
    const Filter = filterComponent === undefined ? ModalFilter : filterComponent;

    useEffect(() => {
        if (apiData.forms_data?.filter && Filter) {
            const handleKeyDown = (ev) => {
                if (ev.key === "Tab") {
                    setShowFilter(true);
                }
            };
            document.addEventListener("keydown", handleKeyDown);
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        }
    }, [apiData.forms_data?.filter]);

    if (apiData.status === "fail" && apiData.errmsg) {
        return null;
    }

    return (
        Object.keys(apiData).length > 0 && (
            <>
                {Filter && (
                    <Filter
                        prefix={prefix}
                        show={showFilter}
                        setShow={setShowFilter}
                        apiData={apiData}
                        setApiData={setApiData}
                    />
                )}
                <ModalDelete />

                <RenderMessages apiData={apiData} />

                <Card>
                    <Card.Header>
                        {header}
                        <div className="dmg-card-header-tools">
                            {Filter && apiData.forms_data?.filter && (
                                <Button href="#" variant="dark" size="sm" onClick={() => setShowFilter(true)}>
                                    <Awesome type="filter" />
                                </Button>
                            )}{" "}
                            {headerTools}
                        </div>
                    </Card.Header>
                    <Card.Body>{children}</Card.Body>
                    <DjangoPaginator pager={apiData.pager} />
                </Card>
            </>
        )
    );
}

export function CommonCard({ children, header, headerTools, errorFunction }) {
    const { apiData } = useAppContext();

    if (apiData.status === "fail" && apiData.errmsg) {
        return null;
    }
    return (
        <Card>
            {header && (
                <Card.Header>
                    {header}
                    <div className="dmg-card-header-tools">{headerTools}</div>
                </Card.Header>
            )}
            <Card.Body>
                <RenderMessages apiData={apiData} />
                <RenderErrors apiData={apiData} errorFunction={errorFunction} />
                {children}
            </Card.Body>
        </Card>
    );
}

export default function RenderErrors({ apiData, errorFunction }) {
    const non_field_errors = [];
    const errors = [];

    const applyScroll = (event, selectedName, locale) => {
        event.preventDefault();
        if (errorFunction) {
            if (is_str(locale)) {
                const prepareLocale = locale.includes("RU") ? "ru_RU" : "en_US";
                errorFunction(selectedName, prepareLocale);
            } else {
                errorFunction(selectedName, "ru_RU");
            }
        }

        let element = document.querySelector(`[name$=${selectedName}]`);
        if (element) {
            if (element.tagName === "DIV") {
                element = element.firstElementChild;
            } else {
                while (element.parentElement && !element.classList.contains("form-group")) {
                    element = element.parentElement;
                }
            }
            ScrollToElement(element);
        }
    };

    for (const [prefix, form] of Object.entries(apiData.forms_data || {})) {
        if (!is_dict(form) || form.prefix !== prefix) {
            continue;
        }

        form.non_field_errors.forEach((err, i) => {
            // тут не надо выводить форму, в которой ошибка произошла, чтобы можно было общие ошибки добавлять
            non_field_errors.push(
                <div key={`non_field_errors${i}`} className="text-danger">
                    <ParseFormErrors err={err} />
                </div>
            );
        });
        for (const [name, err] of Object.entries(form.errors)) {
            if (name in form.fields) {
                errors.push(
                    <div key={prefix + name}>
                        <strong>
                            <a href="#" onClick={(event) => applyScroll(event, name, form.title)}>
                                {form.fields[name].label}
                                {form.title && ` [${form.title}]`}
                            </a>
                        </strong>
                        <div className="ml-3 text-danger">
                            <ParseFormErrors err={err.join("<br>")} />
                        </div>
                    </div>
                );
            }
        }
    }
    return (
        <>
            {Boolean(non_field_errors.length) && (
                <div className="error_collection">
                    {non_field_errors.map((obj) => {
                        return obj;
                    })}
                </div>
            )}
            {Boolean(errors.length) && (
                <div className="error_collection">
                    {errors.map((obj) => {
                        return obj;
                    })}
                </div>
            )}
        </>
    );
}

function RenderMessages({ apiData }) {
    const lmap = {
        error: "danger",
        warning: "warning",
        info: "info",
        success: "success",
    };
    const messages = [];
    if (apiData.messages) {
        ["error", "warning", "info", "success"].forEach((lvl) => {
            apiData.messages[lvl].forEach((o, i) => {
                messages.push(
                    <Alert key={`message_${lvl}_${i}`} variant={lmap[lvl]}>
                        {parseAndLinkify(o)}
                    </Alert>
                );
            });
        });
    }
    return messages;
}
